import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

import { Listbox } from '@/components/ui/listbox';
import { cn } from '@/utils/cn';

/**
 * Page navigation for an offset-paginated collection.
 *
 * Pagination is mandatory on every list in Trinity. A church with eleven thousand
 * members has one list view that will bring a browser down, and it is whichever one
 * shipped without this component.
 */

export interface PaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Server maximum is 100; anything above it is refused rather than clamped. */
  pageSizeOptions?: number[];
  labels: {
    showing: (range: { from: number; to: number; total: number }) => string;
    perPage: string;
    first: string;
    previous: string;
    next: string;
    last: string;
  };
  disabled?: boolean;
  className?: string;
}

export const Pagination = ({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  labels,
  disabled = false,
  className,
}: PaginationProps) => {
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const go = (next: number) => {
    if (disabled) return;
    onPageChange(Math.min(Math.max(next, 1), Math.max(totalPages, 1)));
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <p className="text-body-sm text-fg-secondary">
        {labels.showing({ from, to, total: totalItems })}
      </p>

      <div className="flex items-center gap-3">
        {onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span className="text-body-sm text-fg-secondary">{labels.perPage}</span>
            <Listbox<number>
              value={pageSize}
              onChange={(next) => next && onPageSizeChange(next)}
              options={pageSizeOptions.map((size) => ({ value: size, label: String(size) }))}
              size="sm"
              className="w-20"
              disabled={disabled}
              aria-label={labels.perPage}
            />
          </div>
        ) : null}

        <div className="flex items-center gap-1">
          <PageButton
            label={labels.first}
            onClick={() => go(1)}
            disabled={disabled || page <= 1}
            icon={<ChevronsLeft className="h-4 w-4" />}
          />
          <PageButton
            label={labels.previous}
            onClick={() => go(page - 1)}
            disabled={disabled || page <= 1}
            icon={<ChevronLeft className="h-4 w-4" />}
          />

          <span className="px-2 text-body-sm tabular-nums text-fg">
            {page} / {Math.max(totalPages, 1)}
          </span>

          <PageButton
            label={labels.next}
            onClick={() => go(page + 1)}
            disabled={disabled || page >= totalPages}
            icon={<ChevronRight className="h-4 w-4" />}
          />
          <PageButton
            label={labels.last}
            onClick={() => go(totalPages)}
            disabled={disabled || page >= totalPages}
            icon={<ChevronsRight className="h-4 w-4" />}
          />
        </div>
      </div>
    </div>
  );
};

const PageButton = ({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'rounded-md border border-border p-1.5 text-fg-secondary transition-colors',
      'hover:bg-surface-sunken hover:text-fg',
      'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
    )}
  >
    {icon}
  </button>
);
